import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { isRBACEnabled } from '@/config/rbac'
import type { AuthzRole } from '@/types/AuthzClaims'
import LoginView from '@/views/LoginView.vue'
import HomeView from '@/views/HomeView.vue'
import TransactionListView from '@/views/transaction/TransactionListView.vue'
import TransactionDetailView from '@/views/transaction/TransactionDetailView.vue'
import TransactionFormView from '@/views/transaction/TransactionFormView.vue'
import PurchaseWizardView from '@/views/transaction/PurchaseWizardView.vue'
import TransactionPrintView from '@/views/transaction/TransactionPrintView.vue'
import CommodityView from '@/views/CommodityView.vue'
import PersonListView from '@/views/person/PersonListView.vue'
import OrganizationListView from '@/views/organization/OrganizationListView.vue'
import EmailTemplateListView from '@/views/email/EmailTemplateListView.vue'
import EmailTemplateFormView from '@/views/email/EmailTemplateFormView.vue'
import EmailMessageListView from '@/views/EmailMessageListView.vue'
import CrmPersonView from '@/views/crm/CrmPersonView.vue'
import CrmOrganizationView from '@/views/crm/CrmOrganizationView.vue'
import CrmTaskView from '@/views/crm/CrmTaskView.vue'
import CrmActivityTimelineView from '@/views/CrmActivityTimelineView.vue'

const routes: RouteRecordRaw[] = [
  { path: '/', component: HomeView, meta: { requiresAuth: true } },
  { path: '/login', component: LoginView },

  // Tenant-specific login route
  {
    path: '/login/:slug',
    name: 'TenantLogin',
    component: LoginView,
    meta: { requiresAuth: false },
  },

  // Transaction routes
  { path: '/transaction', component: TransactionListView, meta: { requiresAuth: true } },
  { path: '/transaction/new', component: TransactionFormView, meta: { requiresAuth: true } },
  { path: '/transaction/:id', component: TransactionDetailView, meta: { requiresAuth: true } },
  { path: '/transaction/:id/edit', component: TransactionFormView, meta: { requiresAuth: true } },
  { path: '/transaction/:id/wizard', component: PurchaseWizardView, meta: { requiresAuth: true } },
  { path: '/transaction/:id/print', component: TransactionPrintView, meta: { requiresAuth: true } },

  // Purchase routes
  {
    path: '/purchases/new',
    component: () => import('@/views/transaction/NewPurchaseView.vue'),
    meta: { requiresAuth: true },
  },

  // Sale routes
  {
    path: '/sales/new',
    component: () => import('@/views/transaction/NewSaleView.vue'),
    meta: { requiresAuth: true },
  },

  // Transaction reports
  {
    path: '/transaction/reports',
    component: () => import('@/views/transaction/TransactionReportsView.vue'),
    meta: { requiresAuth: true },
  },

  // Reports routes
  {
    path: '/reports',
    component: () => import('@/views/reports/ReportsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reports/business-overview',
    component: () => import('@/views/reports/BusinessOverviewReport.vue'),
    meta: { requiresAuth: true },
  },

  // Commodity routes
  { path: '/commodity', component: CommodityView, meta: { requiresAuth: true } },

  // Container routes (placeholder)
  {
    path: '/containers',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true },
  },

  // CRM routes
  { path: '/crm/people', component: PersonListView, meta: { requiresAuth: true } },
  { path: '/crm/people/:id', component: CrmPersonView, meta: { requiresAuth: true } },
  { path: '/crm/organizations', component: OrganizationListView, meta: { requiresAuth: true } },
  { path: '/crm/organizations/:id', component: CrmOrganizationView, meta: { requiresAuth: true } },
  { path: '/crm/emails', component: EmailMessageListView, meta: { requiresAuth: true } },
  { path: '/crm/email-templates', component: EmailTemplateListView, meta: { requiresAuth: true } },
  {
    path: '/crm/email-templates/new',
    component: EmailTemplateFormView,
    meta: { requiresAuth: true },
  },
  {
    path: '/crm/email-templates/:id',
    component: EmailTemplateFormView,
    meta: { requiresAuth: true },
  },
  { path: '/crm/emailer', component: () => import('@/views/crm/CrmEmailerView.vue') },
  {
    path: '/crm/email-lists',
    component: () => import('@/views/EmailListListView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/crm/email-lists/:id',
    component: () => import('@/views/EmailListView.vue'),
    meta: { requiresAuth: true },
  },
  { path: '/crm/tasks', component: CrmTaskView },
  { path: '/crm/timeline', component: CrmActivityTimelineView, meta: { requiresAuth: true } },

  // Admin routes
  {
    path: '/admin',
    component: () => import('@/views/admin/AdminLayout.vue'),
    meta: { requiresAuth: true, requiresRole: 'tenantAdmin' },
    children: [
      { path: '', redirect: '/admin/users' },
      { path: 'users', component: () => import('@/views/admin/UserManagementView.vue') },
      { path: 'role-requests', component: () => import('@/views/admin/RoleRequestsView.vue') },
      { path: 'tenants', component: () => import('@/views/admin/TenantManagementView.vue') },
      { path: 'audit', component: () => import('@/views/admin/AuditLogView.vue') },
    ],
  },

  // Other routes
  { path: '/version', component: () => import('@/views/VersionView.vue') },
  { path: '/dev', component: () => import('@/views/DevView.vue') },
  { path: '/reload', component: () => import('@/views/ReloadView.vue') },

  // Settings routes
  {
    path: '/settings',
    component: () => import('@/views/settings/SettingsLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/settings/email' },
      {
        path: 'general',
        component: () => import('@/views/settings/GeneralSettings.vue'),
        meta: { disabled: true },
      },
      {
        path: 'profile',
        component: () => import('@/views/settings/ProfileSettings.vue'),
        meta: { disabled: true },
      },
      { path: 'email', component: () => import('@/views/settings/EmailSettings.vue') },
      { path: 'phone', component: () => import('@/views/settings/PhoneSettings.vue') },
      {
        path: 'crm',
        component: () => import('@/views/settings/CrmSettings.vue'),
        meta: { disabled: true },
      },
      { path: 'transactions', component: () => import('@/views/settings/TransactionSettings.vue') },
      { path: 'commodities', component: () => import('@/views/settings/CommoditySettings.vue') },
      {
        path: 'notifications',
        component: () => import('@/views/settings/NotificationSettings.vue'),
      },
      {
        path: 'security',
        component: () => import('@/views/settings/SecuritySettings.vue'),
        meta: { disabled: true },
      },
      {
        path: 'integrations',
        component: () => import('@/views/settings/IntegrationsSettings.vue'),
      },
      {
        path: 'devices',
        component: () => import('@/views/settings/DeviceSettings.vue'),
      },
      {
        path: 'agents',
        component: () => import('@/views/settings/AgentRegistrationView.vue'),
      },
      {
        path: 'id-documents',
        component: () => import('@/views/settings/IDDocumentSettings.vue'),
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore()

  console.log('🛣️ Router: Navigation guard triggered', {
    to: to.path,
    from: from.path,
    requiresAuth: to.meta.requiresAuth,
    requiresRole: to.meta.requiresRole,
  })

  // Only execute the redirect check if we are on the page designated as the final redirect URL (e.g., /login)
  // AND we're coming from an external source (initial page load or SAML redirect), not from within the app
  // This prevents checking redirect results after logout, which would cause re-authentication
  // from.name is undefined on initial navigation or external redirects
  // from.matched.length === 0 means the previous route wasn't matched (external navigation)
  const isExternalNavigation = from.name === undefined || from.matched.length === 0

  if (to.path === '/login' && !auth.isAuthenticated && isExternalNavigation) {
    console.log('🛣️ Router: Checking for redirect result on login page', {
      from: from.path,
      fromName: from.name,
      matchedLength: from.matched.length,
      isExternalNavigation,
    })
    try {
      const success = await auth.handleRedirectResult()

      if (success) {
        // User successfully logged in via redirect. Redirect to app home.
        console.log('🛣️ Router: User authenticated via redirect, going to home')
        next('/')
        return
      }
    } catch (e) {
      console.error('🛣️ Router: Error checking redirect result', e)
    }
  }

  // Wait for auth store to finish loading
  if (auth.loading) {
    console.log('🛣️ Router: Auth store is loading, waiting...')
    // Wait for loading to complete with a simple timeout
    await new Promise((resolve) => setTimeout(resolve, 100))
  }

  console.log('🛣️ Router: Auth state check', {
    loading: auth.loading,
    isAuthenticated: auth.isAuthenticated,
    hasPersistedAuth: auth.hasPersistedAuth,
    isSaasAdmin: auth.isSaasAdmin,
    hasAuthTenant: auth.hasAuthTenant,
    roles: auth.roles,
  })

  // Check authentication
  if (to.meta.requiresAuth && !auth.isAuthenticated && !auth.hasPersistedAuth) {
    console.log('🛣️ Router: Redirecting to login - not authenticated')
    next('/login')
    return
  }

  // Admin routes require is_saas_admin
  if (to.path.startsWith('/admin') && !auth.isSaasAdmin) {
    console.log('🛣️ Router: Redirecting to home because user is not a SaaS admin')
    next('/')
    return
  }

  // Non-admin routes require auth tenant (unless SaaS admin or login routes)
  if (
    !to.path.startsWith('/admin') &&
    !to.path.startsWith('/login') &&
    !auth.hasAuthTenant &&
    !auth.isSaasAdmin
  ) {
    console.log('🛣️ Router: Redirecting to login - no auth tenant')
    next('/login')
    return
  }

  // Check role requirements (only if RBAC is enabled)
  // SaaS admins can bypass role requirements
  if (
    isRBACEnabled() &&
    to.meta.requiresRole &&
    !auth.isSaasAdmin &&
    !auth.hasRole(to.meta.requiresRole as AuthzRole)
  ) {
    console.log('🛣️ Router: Redirecting to home - insufficient role permissions')
    next('/') // Redirect to home if insufficient permissions
    return
  }

  console.log('🛣️ Router: Navigation allowed, proceeding to', to.path)
  next()
})

export default router
